import { navigate } from 'hookrouter';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { KNOWN_ERROR_CODES } from '../../../api/constants';
import { KnownError } from '../../../api/errors';
import { activateAuthenticator } from '../../../api/fetch/activate-authenticator';
import { enrollAuthenticator } from '../../../api/fetch/enroll-authenticator';
import { ActivateAccountMessage } from '../../../components/ui/ActivateAccountMessage';
import { EmailVerification } from '../../../components/ui/EmailVerification';
import { PageLayout } from '../../../components/ui/PageLayout';
import { WelcomeMGMRewards } from '../../../components/ui/WelcomeMGMRewards';
import { usePasskeyEnabled } from '../../../hooks';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { useCountdown } from '../../../hooks/useCountdown';
import { useInput } from '../../../hooks/useInput';
import { useLoadingLabel } from '../../../hooks/useLoadingLabel';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import {
  defaultProfile2FAPath,
  IDW_FLOW_PARAM,
  IDENTITY_WIDGET_PATHS,
  MFA_ENROLL_STATUS_PARAM,
} from '../../../utils/constants';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { useUserAccount } from '../../../utils/userAccountContext';
import { MfaEnrollmentFlowType } from '../Enrollment/types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function EmailVerificationPage({ type }: { type: 'enrollment' | 'authentication' }) {
  const redirectToApp = useAppRedirect();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const { passkeyEnabled } = usePasskeyEnabled();
  const eventNameError = getContentByPath<string>('mfa.analytics.error.eventName');
  const trackSuffixError = getContentByPath<string>('mfa.analytics.error.trackSuffix');
  usePageLoadEvent('identity', '2fa setup: email auth code');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setLoadingState] = useState(false);
  const submitCTA = useLoadingLabel(isLoading, 'EmailVerificationPage.submit');
  const {
    bind: bindVerificationCode,
    value: verificationCode,
    reset: resetVerificationCode,
  } = useInput('', 'verificationCode');
  const [authenticatorActivated, setAuthenticatorActivated] = useState('');
  const [inputError, setInputError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [codeResentSuccessfully, setCodeResentSuccessfully] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const enrollmentAttempted = useRef(false);
  const { email } = useUserAccount();
  const [showWelcomeMgmRewardsScreen, setShowWelcomeMgmRewardsScreen] = useState(false);
  const [showAccountActivatedScreen, setShowAccountActivatedScreen] = useState(false);
  const params = getPersistedParameters();
  const idwFlow: MfaEnrollmentFlowType = params?.[IDW_FLOW_PARAM] as MfaEnrollmentFlowType;

  const countDown = useCountdown(30);

  const enrollEmail = useCallback(async () => {
    try {
      if (isEnrolled || enrollmentAttempted.current) {
        return;
      }

      enrollmentAttempted.current = true;

      const payload = {
        type: 'email',
        email: email,
      };

      // enrollAuthenticator extracts user ID from the JWT token internally,
      // so we don't need to gate on mgmId from context (which may not be populated in sign-in flow)
      const response = await enrollAuthenticator(payload);
      setOpenAlert(true);
      setCodeResentSuccessfully(true);
      setAuthenticatorActivated(response.id ?? '');
      setIsEnrolled(true);
      countDown.startCountdown();
      const message = getContentByPath('mfa.email.codeResentSuccess') as string;
      await trackEvent(
        'identity – unified MFA notification',
        message,
        'unifiedmfanotification',
        { notificationDisplayed: message },
        true,
      );
    } catch (err) {
      let message;
      if (err instanceof KnownError) {
        if (err.code === 'E041') {
          message = KNOWN_ERROR_CODES.E041;
        }
      } else {
        message = getContentByPath('mfa.email.systemError');
      }
      setOpenAlert(true);
      setErrorMessage(message);
      trackEvent(eventNameError, message, trackSuffixError, {}, true);
      enrollmentAttempted.current = false;
    }
  }, [email, getContentByPath, isEnrolled, countDown, eventNameError, trackEvent, trackSuffixError]);

  useEffect(() => {
    if (email && !enrollmentAttempted.current && !isEnrolled) {
      enrollEmail();
    }
  }, [email, enrollEmail, isEnrolled]);

  const validateCode = useCallback(() => {
    setTouched(true);
    if (verificationCode.length === 0) {
      setInputError(getContentByPath('mfa.email.fields.verificationCode.required'));
      trackEvent(
        eventNameError,
        getContentByPath('mfa.email.fields.verificationCode.required'),
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    if (verificationCode.length > 0 && verificationCode.length < 6) {
      setInputError(getContentByPath('mfa.email.fields.verificationCode.invalid'));
      trackEvent(
        eventNameError,
        getContentByPath('mfa.email.fields.verificationCode.invalid'),
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    setInputError('');
    return true;
  }, [verificationCode, getContentByPath, trackEvent, eventNameError, trackSuffixError]);

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: numericValue },
    };
    bindVerificationCode.onChange(syntheticEvent);

    if (touched) {
      if (numericValue.length === 6) {
        setInputError('');
      } else if (numericValue.length > 0 && numericValue.length < 6) {
        setInputError(getContentByPath('mfa.email.fields.verificationCode.invalid'));
      }
    }
  };

  const submitVerificationCode = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Submit Code - Email authentication', 'unifiedmfactas', {}, true);
    try {
      const payload = { passCode: verificationCode };
      const authenticatorId = authenticatorActivated;

      if (!authenticatorId) {
        throw new Error('Enrollment not completed. Please try again.');
      }

      // activateAuthenticator extracts user ID from the JWT token internally,
      // so we don't need to gate on mgmId from context
      const response = await activateAuthenticator(authenticatorId, payload);
      console.log('Email authenticator activated successfully', response);

      await trackEvent(
        'identity – unified MFA notification',
        'Email authentication has been added',
        'unifiedmfanotification',
        { notificationDisplayed: 'Email authentication has been added' },
        true,
      );
    } catch (err) {
      setCodeResentSuccessfully(false);

      const errorField = 'Verification Code';
      let errorDisplayed = 'The provided passcode is incorrect or has expired';

      if (err instanceof KnownError) {
        const errorCode = err.code;
        switch (errorCode) {
          case 'E008':
            setInputError(KNOWN_ERROR_CODES.E0000068);
            errorDisplayed = KNOWN_ERROR_CODES.E0000068;
            break;

          case 'E34':
            setErrorMessage(KNOWN_ERROR_CODES.E34);
            errorDisplayed = KNOWN_ERROR_CODES.E34;
            break;

          case 'E042':
            setErrorMessage(KNOWN_ERROR_CODES.E042);
            errorDisplayed = KNOWN_ERROR_CODES.E042;
            break;

          case 'E054':
            setErrorMessage(KNOWN_ERROR_CODES.E054);
            errorDisplayed = KNOWN_ERROR_CODES.E054;
            break;

          case 'E043':
            setErrorMessage(KNOWN_ERROR_CODES.E043);
            errorDisplayed = KNOWN_ERROR_CODES.E043;
            break;

          default:
            setErrorMessage(getContentByPath('mfa.email.systemError'));
            errorDisplayed = 'System Error';
            break;
        }
      } else {
        setErrorMessage(getContentByPath('mfa.email.systemError'));
        errorDisplayed = 'System Error';
      }
      await trackEvent(eventNameError, errorDisplayed, trackSuffixError, { errorField, errorDisplayed }, true);
      throw err;
    }
  }, [verificationCode, authenticatorActivated, trackEvent, getContentByPath, eventNameError, trackSuffixError]);

  const handleSubmit = useCallback(async () => {
    if (!validateCode()) {
      return;
    }
    setLoadingState(true);
    try {
      await submitVerificationCode();
      let queryParams = {};
      if (type === 'enrollment') {
        // If passkey is enabled and this is a signup/activate flow, navigate to passkey enrollment post-2FA
        if (passkeyEnabled && (idwFlow === 'signup' || idwFlow === 'activate')) {
          navigate(IDENTITY_WIDGET_PATHS.MfaEnrollPasskey, true, { [IDW_FLOW_PARAM]: idwFlow });
          return;
        } else if (idwFlow === 'signin') {
          // For sign-in flow, redirect immediately after successful enrollment
          redirectToApp({}, true);
          return;
        } else {
          if (idwFlow === 'activate') {
            // Display activation success screen
            setShowAccountActivatedScreen(true);
          } else if (idwFlow === 'signup') {
            setShowWelcomeMgmRewardsScreen(true);
          }
        }

        if (
          document?.referrer?.includes(defaultProfile2FAPath) ||
          params?.redirect_uri?.includes(defaultProfile2FAPath)
        ) {
          queryParams = { [MFA_ENROLL_STATUS_PARAM]: '2fa-email-added' };
        }
      }
      // Add 2-second delay before redirecting to app
      setTimeout(() => {
        redirectToApp(queryParams, true);
      }, 2000);
    } catch (err) {
      setLoadingState(false);
      console.error('Error during email authentication:', err);
    }
  }, [
    validateCode,
    submitVerificationCode,
    redirectToApp,
    type,
    setShowWelcomeMgmRewardsScreen,
    setShowAccountActivatedScreen,
    idwFlow,
    params?.redirect_uri,
    passkeyEnabled,
  ]);

  const handleResendCode = async () => {
    if (!countDown.isRunning) {
      try {
        await trackEvent(
          'identity – unified MFA CTAs',
          'Resend Code - Email authentication',
          'unifiedmfactas',
          {},
          true,
        );

        // Clear any previous error states
        setErrorMessage('');
        setInputError('');
        setTouched(false);

        // Reset the verification code input field
        resetVerificationCode();

        // Reset enrollment flags to allow re-enrollment
        enrollmentAttempted.current = false;
        setIsEnrolled(false);

        await enrollEmail();
        setCodeResentSuccessfully(true);
        countDown.startCountdown();
      } catch (err) {
        setCodeResentSuccessfully(false);
        setOpenAlert(true);
        setErrorMessage(getContentByPath('mfa.email.codeResentFail'));
        await trackEvent(eventNameError, getContentByPath('mfa.email.codeResentFail'), trackSuffixError, {}, true);
      }
    }
  };

  const maskEmail = (email: string) => {
    return email.replace(/^(.)(.*)(.)@/, (_, first, middle, last) => `${first}...${last}@`);
  };

  const onCloseAlert = () => {
    setOpenAlert(false);
    setErrorMessage('');
  };
  const handleVerificationCodeBlur = () => {
    validateCode();
  };

  return (
    <div data-testid='route-/mfa/enroll/email'>
      {showAccountActivatedScreen ? (
        <PageLayout>
          <ActivateAccountMessage />
        </PageLayout>
      ) : showWelcomeMgmRewardsScreen ? (
        <PageLayout>
          <WelcomeMGMRewards />
        </PageLayout>
      ) : (
        <EmailVerification
          onCloseAlert={onCloseAlert}
          email={maskEmail(email)}
          verificationCode={verificationCode}
          onVerificationCodeChange={handleVerificationCodeChange}
          onVerificationCodeBlur={handleVerificationCodeBlur}
          onSubmit={handleSubmit}
          onResend={handleResendCode}
          isLoading={isLoading}
          inputError={!!inputError}
          inputHint={inputError}
          isCountdownRunning={countDown.isRunning}
          secondsLeft={countDown.secondsLeft}
          submitButtonLabel={submitCTA || getContentByPath('mfa.email.submit')}
          codeResentSuccessfully={codeResentSuccessfully}
          errorMessage={errorMessage}
          openAlert={openAlert}
          type={type}
        />
      )}
    </div>
  );
}

EmailVerificationPage.displayName = 'EmailVerificationPage';
